import React from 'react';
import { Helmet } from 'react-helmet';
import { Box, Heading, Text, Button } from '@indeed/ifl-components';
import { GettextInterface } from '@indeed/indeed-gettext';

import PageTransition from '../../../components/PageTransition/PageTransition';
import JobDescription from '../../../components/JobDescription/JobDescription';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';

export interface ResumePageProps {
    step?: string;
    gt?: GettextInterface;
}

const ResumePage: React.FunctionComponent<ResumePageProps> = () => {
    const { goBack, canGoBack, goForward } = usePageFlow();

    const handleBack = (): void => {
        void goBack();
    };

    const handleContinue = (): void => {
        void goForward(); // Use flow-aware navigation
    };

    return (
        <PageTransition pageKey="resume">
            <Helmet>
                <title>Resume Upload - Indeed</title>
                <meta
                    name="description"
                    content="Upload your resume and showcase your work experience for your job application with Indeed."
                />
                <meta
                    name="keywords"
                    content="resume upload, work experience, job application, Indeed, careers"
                />
            </Helmet>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: ['1fr', '1fr 1fr'],
                    gap: 6,
                    alignItems: 'start',
                }}
            >
                {/* Left Section - Resume Upload */}
                <Box
                    sx={{
                        backgroundColor: 'background.default',
                        padding: 6,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'border.subtle',
                    }}
                >
                    <Heading
                        as="h2"
                        level={2}
                        sx={{
                            color: 'foreground.primary',
                            marginBlockEnd: 2,
                        }}
                    >
                        Resume & Experience
                    </Heading>
                    <Text sx={{ color: 'foreground.secondary', fontSize: 2, marginBlockEnd: 6 }}>
                        Upload your resume or enter your work experience manually.
                    </Text>

                    <Box sx={{ marginBlockEnd: 6 }}>
                        <Heading
                            as="h3"
                            level={4}
                            sx={{ marginBlockEnd: 4, color: 'foreground.primary' }}
                        >
                            Upload Resume
                        </Heading>
                        <Box
                            sx={{
                                border: '2px dashed',
                                borderColor: 'border.subtle',
                                borderRadius: 2,
                                padding: 8,
                                textAlign: 'center',
                                backgroundColor: 'background.subtle',
                                cursor: 'pointer',
                                '&:hover': {
                                    borderColor: 'primary.default',
                                },
                            }}
                        >
                            <Text sx={{ color: 'foreground.secondary', marginBlockEnd: 3 }}>
                                Drag and drop your resume here, or click to browse
                            </Text>
                            <Button variant="secondary">Choose File</Button>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
                        {canGoBack && (
                            <Button variant="secondary" onClick={handleBack}>
                                Back
                            </Button>
                        )}
                        <Button variant="primary" onClick={handleContinue}>
                            Continue
                        </Button>
                    </Box>
                </Box>

                {/* Right Section - Resume Tips & Job Description */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {/* Resume Tips */}
                    <Box
                        sx={{
                            backgroundColor: 'background.default',
                            padding: 6,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'border.subtle',
                        }}
                    >
                        <Heading
                            as="h3"
                            level={3}
                            sx={{
                                color: 'foreground.primary',
                                marginBlockEnd: 4,
                            }}
                        >
                            Resume Tips
                        </Heading>
                        <Box as="ul" sx={{ paddingInlineStart: 4 }}>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Keep your resume to 1-2 pages maximum
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Use clear, professional formatting
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Include relevant keywords from the job description
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Quantify your achievements with numbers
                            </Text>
                            <Text as="li" sx={{ color: 'foreground.secondary', marginBlockEnd: 2 }}>
                                Supported formats: PDF, DOC, DOCX
                            </Text>
                        </Box>
                    </Box>

                    {/* Job Description */}
                    <JobDescription />
                </Box>
            </Box>
        </PageTransition>
    );
};

export default ResumePage;
